
import React, { useEffect } from 'react';
import { useEnrollmentHistory } from '@/hooks/useEnrollmentHistory';
import { EnrollmentEventType } from '@/services/db/enrollment/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EnrollmentEventsList } from './EnrollmentEventsList';

interface EnrollmentHistoryTabProps {
  studentId: number;
}

export const EnrollmentHistoryTab: React.FC<EnrollmentHistoryTabProps> = ({ studentId }) => {
  const { events, loading, fetchHistory } = useEnrollmentHistory(studentId);
  
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);
  
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Enrollment History</CardTitle>
          <CardDescription>Loading enrollment history...</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  
  if (!events || events.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Enrollment History</CardTitle>
          <CardDescription>No enrollment events found.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Enrollment events will appear here once they are recorded.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Enrollment History</CardTitle>
        <CardDescription>
          A record of all enrollment activities for this student
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="all">All Events</TabsTrigger>
            <TabsTrigger value="enrollment">Enrollments</TabsTrigger>
            <TabsTrigger value="transfers">Transfers</TabsTrigger>
            <TabsTrigger value="status">Status Changes</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all">
            <EnrollmentEventsList events={events} />
          </TabsContent>
          
          <TabsContent value="enrollment">
            <EnrollmentEventsList 
              events={events} 
              filter={[EnrollmentEventType.NEW_ENROLLMENT, EnrollmentEventType.RE_ENROLLMENT]} 
            />
          </TabsContent>
          
          <TabsContent value="transfers">
            <EnrollmentEventsList 
              events={events} 
              filter={[EnrollmentEventType.PROGRAM_TRANSFER]} 
            />
          </TabsContent>
          
          <TabsContent value="status">
            <EnrollmentEventsList 
              events={events} 
              filter={[EnrollmentEventType.STATUS_CHANGE, EnrollmentEventType.WITHDRAWAL]} 
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default EnrollmentHistoryTab;
